// Atribución de primer contacto para leads web (SEO → leads).
//
// Sin esto, el panel SEO solo mide clics e impresiones: no puede decir qué
// páginas orgánicas terminan en un lead ni en una cotización ganada. Se
// captura UNA vez (primera página que ve el visitante en la sesión de 30 días)
// y se envía junto al formulario, que puede estar varias páginas después.
//
// La parte pura (parseAttribution / classifyChannel / sanitizeAttribution) se
// comparte con el servidor, que recalcula el canal en vez de confiar en el
// cliente.

export const CANALES = ["organico", "pago", "social", "email", "referido", "directo"] as const;
export type Canal = (typeof CANALES)[number];

export interface Attribution {
  landing_page: string | null;
  referrer: string | null; // solo host, sin path ni query
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  gclid: string | null;
  canal: Canal;
}

const SEARCH_ENGINES = [
  "google.",
  "bing.",
  "duckduckgo.",
  "yahoo.",
  "ecosia.",
  "yandex.",
  "brave.",
];
const SOCIAL_HOSTS = [
  "facebook.com",
  "instagram.com",
  "linkedin.com",
  "lnkd.in",
  "t.co",
  "twitter.com",
  "x.com",
  "tiktok.com",
  "youtube.com",
  "wa.me",
  "whatsapp.com",
  "pinterest.",
];
const PAID_MEDIUMS = ["cpc", "ppc", "paid", "paidsearch", "paid_social", "display", "sem"];

const MAX = { path: 200, host: 120, utm: 100, gclid: 120 } as const;

const clip = (v: string | null | undefined, n: number): string | null => {
  const t = (v ?? "").trim();
  return t ? t.slice(0, n) : null;
};

const hostMatches = (host: string, list: string[]) =>
  list.some((h) => (h.endsWith(".") ? host.includes(h) : host === h || host.endsWith(`.${h}`)));

export function classifyChannel(a: {
  referrer: string | null;
  utm_medium: string | null;
  gclid: string | null;
}): Canal {
  const medium = (a.utm_medium ?? "").toLowerCase();
  if (a.gclid || PAID_MEDIUMS.includes(medium)) return "pago";
  if (medium === "email" || medium === "e-mail") return "email";
  if (medium === "organic") return "organico";
  if (medium === "social") return "social";
  const host = (a.referrer ?? "").toLowerCase();
  if (!host) return "directo";
  // El correo web va antes que los buscadores: mail.google.com contiene "google.".
  if (host.startsWith("mail.") || host.startsWith("outlook.")) return "email";
  if (hostMatches(host, SEARCH_ENGINES)) return "organico";
  if (hostMatches(host, SOCIAL_HOSTS)) return "social";
  return "referido";
}

/** Atribución a partir de la URL de aterrizaje y document.referrer. */
export function parseAttribution(input: {
  url: string;
  referrer: string;
  ownHosts: string[];
}): Attribution {
  let u: URL;
  try {
    u = new URL(input.url);
  } catch {
    return {
      landing_page: null,
      referrer: null,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      gclid: null,
      canal: "directo",
    };
  }

  let refHost: string | null;
  try {
    const h = input.referrer ? new URL(input.referrer).hostname.toLowerCase() : "";
    // Un referrer de nuestro propio dominio no es una fuente de adquisición.
    refHost = h && !input.ownHosts.some((o) => h === o || h.endsWith(`.${o}`)) ? h : null;
  } catch {
    refHost = null;
  }

  const q = u.searchParams;
  const base = {
    landing_page: clip(u.pathname, MAX.path),
    referrer: clip(refHost, MAX.host),
    utm_source: clip(q.get("utm_source"), MAX.utm),
    utm_medium: clip(q.get("utm_medium"), MAX.utm),
    utm_campaign: clip(q.get("utm_campaign"), MAX.utm),
    gclid: clip(q.get("gclid"), MAX.gclid),
  };
  return { ...base, canal: classifyChannel(base) };
}

/** Validación del lado servidor: recorta longitudes y recalcula el canal. */
export function sanitizeAttribution(raw: unknown): Attribution | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const s = (k: string, n: number) => clip(typeof r[k] === "string" ? (r[k] as string) : null, n);
  const base = {
    landing_page: s("landing_page", MAX.path),
    referrer: s("referrer", MAX.host),
    utm_source: s("utm_source", MAX.utm),
    utm_medium: s("utm_medium", MAX.utm),
    utm_campaign: s("utm_campaign", MAX.utm),
    gclid: s("gclid", MAX.gclid),
  };
  return { ...base, canal: classifyChannel(base) };
}

// ── Cliente (localStorage) ───────────────────────────────────────────────────

const STORAGE_KEY = "btl_attr_v1";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface Stored {
  at: number;
  attribution: Attribution;
}

/** Guarda el primer contacto; el gclid más reciente siempre se actualiza (Ads usa last-click). */
export function captureFirstTouch(ownHosts: string[]): void {
  try {
    const now = Date.now();
    const current = parseAttribution({
      url: window.location.href,
      referrer: document.referrer,
      ownHosts,
    });
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const prev: Stored | null = raw ? (JSON.parse(raw) as Stored) : null;
    if (prev && now - prev.at < TTL_MS) {
      if (current.gclid && current.gclid !== prev.attribution.gclid) {
        prev.attribution = { ...prev.attribution, gclid: current.gclid };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prev));
      }
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ at: now, attribution: current }));
  } catch {
    // Storage bloqueado (modo privado, etc.): se degrada a la atribución de la página actual.
  }
}

export function getStoredAttribution(ownHosts: string[]): Attribution {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const s = JSON.parse(raw) as Stored;
      if (Date.now() - s.at < TTL_MS) return s.attribution;
    }
  } catch {
    // ver captureFirstTouch
  }
  return parseAttribution({ url: window.location.href, referrer: document.referrer, ownHosts });
}

/** Hosts propios del sitio público, para ignorar la navegación interna como referrer. */
export const OWN_HOSTS = ["improvey.agency", "localhost", "127.0.0.1"];
